export interface Coordinates {
  latitude: number
  longitude: number
}

export type GeolocationFailure = 'unsupported' | 'denied' | 'unavailable' | 'timeout'

export class GeolocationError extends Error {
  readonly reason: GeolocationFailure

  constructor(reason: GeolocationFailure) {
    super(`geolocation:${reason}`)
    this.name = 'GeolocationError'
    this.reason = reason
  }
}

export function geolocationErrorKey(error: unknown): string {
  if (error instanceof GeolocationError) {
    switch (error.reason) {
      case 'unsupported':
        return 'profileSocial.profile.location.errors.unsupported'
      case 'denied':
        return 'profileSocial.profile.location.errors.denied'
      case 'timeout':
        return 'profileSocial.profile.location.errors.timeout'
      default:
        return 'profileSocial.profile.location.errors.unavailable'
    }
  }
  return 'profileSocial.profile.location.errors.unavailable'
}

/**
 * Foreground-only browser/Tauri geolocation, requested solely after an
 * explicit "use current location" action. Mirrors the frozen client fix:
 * city-level accuracy, reuse a recent fix, 20 s browser timeout and a 21 s
 * outer guard. Coordinates are used once for reverse geocoding and are never
 * persisted or logged.
 */
export const GEOLOCATION_TIMEOUT_MS = 20_000
export const GEOLOCATION_MAXIMUM_AGE_MS = 60_000
const OUTER_TIMEOUT_MS = 21_000

export function currentCoordinates(
  options: PositionOptions = {
    enableHighAccuracy: false,
    timeout: GEOLOCATION_TIMEOUT_MS,
    maximumAge: GEOLOCATION_MAXIMUM_AGE_MS,
  },
): Promise<Coordinates> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.geolocation ||
    typeof navigator.geolocation.getCurrentPosition !== 'function'
  ) {
    return Promise.reject(new GeolocationError('unsupported'))
  }

  return new Promise((resolve, reject) => {
    let settled = false
    const outerTimer = setTimeout(() => {
      if (settled) return
      settled = true
      reject(new GeolocationError('timeout'))
    }, OUTER_TIMEOUT_MS)

    const finish = (callback: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(outerTimer)
      callback()
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        finish(() => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        })
      },
      (error) => {
        finish(() => {
          if (error.code === error.PERMISSION_DENIED) {
            reject(new GeolocationError('denied'))
            return
          }
          if (error.code === error.TIMEOUT) {
            reject(new GeolocationError('timeout'))
            return
          }
          reject(new GeolocationError('unavailable'))
        })
      },
      options,
    )
  })
}
